import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../Billing/billingOverview.utils";
import {
  GEOGRAPHY_MAP_METRICS,
  formatPercent,
  buildTenantGeographyLegendSteps,
} from "./tenantGeography.utils";
import {
  buildStateOutlinePath,
  buildStateRegionBubbles,
  buildTenantsListPathForRegion,
  computeStateMapBounds,
  formatStateGeographySummary,
  getStateGeographyMetricMax,
  loadPostcodeCoordinateIndex,
} from "./tenantGeographyState.utils";

const MetricToggle = ({ options, value, onChange }) => (
  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        onClick={() => onChange(option.id)}
        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
          value === option.id
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const RegionTooltip = ({ bubble, currency, revenueAvailable }) => {
  if (!bubble) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-10 max-w-xs rounded-lg border border-gray-200 bg-white/95 px-3 py-2 text-xs shadow-sm backdrop-blur">
      <div className="font-semibold text-gray-900">{bubble.label}</div>
      <div className="mt-1 space-y-0.5 text-gray-600">
        <div>
          {bubble.count} tenant{bubble.count === 1 ? "" : "s"} (
          {formatPercent(bubble.tenantSharePct)} of state)
        </div>
        {bubble.count ? (
          <div>
            {bubble.active} active
            {bubble.suspended ? ` · ${bubble.suspended} suspended` : ""}
            {bubble.onTrial ? ` · ${bubble.onTrial} on trial` : ""}
          </div>
        ) : null}
        {revenueAvailable ? (
          <>
            <div>
              {bubble.payingTenants} paying · Est. MRR{" "}
              {formatCurrency(bubble.estimatedMrrCents, currency)}
            </div>
            <div>
              Collected {formatCurrency(bubble.collectedAllTimeCents, currency)} (
              {formatPercent(bubble.revenueSharePct)} of state)
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

const TenantsGeographyStateView = ({
  stateGeography,
  loading = false,
  canListTenants = false,
  canViewBilling = false,
  mapMetricOptions,
  mapMetric,
  onMapMetricChange,
  onBack,
}) => {
  const navigate = useNavigate();
  const [hoveredRegionKey, setHoveredRegionKey] = useState(null);
  const [focusedRegionKey, setFocusedRegionKey] = useState(null);
  const [coordinateIndex, setCoordinateIndex] = useState({ byRegionKey: {}, byPostcode: {} });
  const [coordinatesLoading, setCoordinatesLoading] = useState(false);

  const stateCode = stateGeography?.state_code;
  const revenueAvailable = Boolean(stateGeography?.revenue_available && canViewBilling);
  const currency = stateGeography?.currency || "AUD";

  useEffect(() => {
    if (!stateCode) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setCoordinatesLoading(true);

      try {
        const index = await loadPostcodeCoordinateIndex(stateCode);
        if (!cancelled) {
          setCoordinateIndex(index);
        }
      } catch {
        if (!cancelled) {
          setCoordinateIndex({ byRegionKey: {}, byPostcode: {} });
        }
      } finally {
        if (!cancelled) {
          setCoordinatesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stateCode]);

  const bounds = useMemo(
    () => computeStateMapBounds(stateCode),
    [stateCode]
  );

  const outlinePath = useMemo(
    () => buildStateOutlinePath(stateCode, bounds),
    [stateCode, bounds]
  );

  const bubbles = useMemo(
    () =>
      buildStateRegionBubbles(stateGeography, coordinateIndex, bounds, {
        metric: mapMetric,
        revenueAvailable,
      }),
    [stateGeography, coordinateIndex, bounds, mapMetric, revenueAvailable]
  );

  const metricMax = useMemo(
    () => getStateGeographyMetricMax(stateGeography, mapMetric),
    [stateGeography, mapMetric]
  );

  const legendSteps = useMemo(
    () =>
      buildTenantGeographyLegendSteps(metricMax, {
        revenue: mapMetric !== GEOGRAPHY_MAP_METRICS.TENANTS,
      }),
    [mapMetric, metricMax]
  );

  const regions = stateGeography?.regions || [];
  const revenueTotals = stateGeography?.revenue_totals || {};
  const activeBubble =
    bubbles.find((bubble) => bubble.regionKey === (hoveredRegionKey || focusedRegionKey)) || null;

  const unmappedRegions = Math.max(0, regions.length - bubbles.length);
  const legendTitle =
    mapMetric === GEOGRAPHY_MAP_METRICS.TENANTS
      ? "Tenants"
      : mapMetric === GEOGRAPHY_MAP_METRICS.MRR
        ? "Est. MRR"
        : "Collected";

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 h-4 w-56 animate-pulse rounded bg-gray-200" />
        <div className="aspect-[5/4] animate-pulse rounded-lg bg-gray-100" />
      </div>
    );
  }

  if (!stateGeography) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
        State geography is unavailable right now.
      </div>
    );
  }

  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <button
              type="button"
              onClick={onBack}
              className="font-medium text-indigo-600 hover:text-indigo-700"
            >
              ← Australia
            </button>
            <span>/</span>
            <span className="font-semibold text-gray-700">{stateGeography.state_code}</span>
          </div>
          <h2 className="text-sm font-semibold text-gray-900">
            {stateGeography.state_name} · regional breakdown
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {formatStateGeographySummary(stateGeography)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mapMetricOptions.length > 1 ? (
            <MetricToggle
              options={mapMetricOptions}
              value={mapMetric}
              onChange={onMapMetricChange}
            />
          ) : null}
          {canListTenants && stateCode ? (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/tenants?${new URLSearchParams({ state: stateCode, status: "all" }).toString()}`
                )
              }
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              All {stateCode} tenants →
            </button>
          ) : null}
        </div>
      </div>

      {revenueAvailable ? (
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {[
            {
              label: "Est. MRR",
              value: formatCurrency(revenueTotals.estimated_mrr_cents, currency),
            },
            {
              label: "Collected (all)",
              value: formatCurrency(revenueTotals.collected_all_time_cents, currency),
            },
            {
              label: "Collected (30d)",
              value: formatCurrency(revenueTotals.collected_last_30_days_cents, currency),
            },
            {
              label: "Outstanding",
              value: formatCurrency(revenueTotals.outstanding_cents, currency),
            },
            {
              label: "Paying tenants",
              value: String(revenueTotals.paying_tenants ?? 0),
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
            >
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {item.label}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-gray-900">{item.value}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="relative overflow-hidden rounded-lg border border-gray-100 bg-slate-50">
          <svg
            viewBox={bounds.viewBox}
            className="h-auto w-full"
            role="img"
            aria-label={`Tenant distribution by area in ${stateGeography.state_name}`}
          >
            {outlinePath ? (
              <path
                d={outlinePath}
                fill="#E2E8F0"
                stroke="#FFFFFF"
                strokeWidth={2}
                pointerEvents="none"
              />
            ) : null}

            {bubbles.map((bubble) => {
              const isActive = bubble.regionKey === (hoveredRegionKey || focusedRegionKey);
              const isInteractive = canListTenants;

              return (
                <g
                  key={bubble.regionKey}
                  role={isInteractive ? "link" : undefined}
                  tabIndex={isInteractive ? 0 : undefined}
                  className={isInteractive ? "cursor-pointer" : undefined}
                  onClick={() => {
                    if (isInteractive) {
                      navigate(
                        buildTenantsListPathForRegion({
                          stateCode,
                          suburb: bubble.suburb,
                          postcode: bubble.postcode,
                        })
                      );
                    }
                  }}
                  onKeyDown={(event) => {
                    if (isInteractive && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      navigate(
                        buildTenantsListPathForRegion({
                          stateCode,
                          suburb: bubble.suburb,
                          postcode: bubble.postcode,
                        })
                      );
                    }
                  }}
                  onMouseEnter={() => setHoveredRegionKey(bubble.regionKey)}
                  onMouseLeave={() => setHoveredRegionKey(null)}
                  onFocus={() => setFocusedRegionKey(bubble.regionKey)}
                  onBlur={() => setFocusedRegionKey(null)}
                >
                  <circle
                    cx={bubble.x}
                    cy={bubble.y}
                    r={bubble.radius}
                    fill={bubble.fill}
                    stroke={isActive ? "#312E81" : "#FFFFFF"}
                    strokeWidth={isActive ? 2.5 : 1.5}
                    opacity={hoveredRegionKey && bubble.regionKey !== hoveredRegionKey ? 0.72 : 0.92}
                  />
                  {bubble.count ? (
                    <text
                      x={bubble.x}
                      y={bubble.y + 3}
                      textAnchor="middle"
                      className="pointer-events-none select-none fill-white text-[10px] font-semibold"
                    >
                      {bubble.count}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>

          <RegionTooltip
            bubble={activeBubble}
            currency={currency}
            revenueAvailable={revenueAvailable}
          />

          <div className="absolute right-3 top-3 rounded-lg border border-gray-200 bg-white/95 px-2 py-2 text-[10px] shadow-sm">
            <div className="mb-1 font-semibold uppercase tracking-wide text-gray-400">
              {legendTitle}
            </div>
            <div className="space-y-1">
              {legendSteps.map((step) => (
                <div key={step.label} className="flex items-center gap-2 text-gray-600">
                  <span
                    className="inline-block h-3 w-3 rounded-sm border border-white"
                    style={{ backgroundColor: step.color }}
                  />
                  <span>{step.label}</span>
                </div>
              ))}
            </div>
          </div>

          {coordinatesLoading ? (
            <div className="absolute left-3 top-3 rounded bg-white/90 px-2 py-1 text-[10px] text-gray-500">
              Loading map coordinates…
            </div>
          ) : null}
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Tenants by area
          </div>
          <div className="max-h-[28rem] overflow-auto rounded-lg border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-semibold">Area</th>
                  <th className="px-3 py-2 font-semibold text-right">Total</th>
                  <th className="px-3 py-2 font-semibold text-right">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {regions.map((region) => (
                  <tr
                    key={region.region_key}
                    className={
                      canListTenants ? "cursor-pointer hover:bg-indigo-50/60" : ""
                    }
                    onMouseEnter={() => setHoveredRegionKey(region.region_key)}
                    onMouseLeave={() => setHoveredRegionKey(null)}
                    onClick={() => {
                      if (canListTenants) {
                        navigate(
                          buildTenantsListPathForRegion({
                            stateCode,
                            suburb: region.suburb,
                            postcode: region.postcode,
                          })
                        );
                      }
                    }}
                  >
                    <td className="px-3 py-2 font-medium text-gray-900">{region.label}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      {region.total}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {formatPercent(region.tenant_share_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {unmappedRegions > 0 ? (
        <p className="mt-3 text-xs text-amber-700">
          {unmappedRegions} area{unmappedRegions === 1 ? "" : "s"} could not be placed on the map
          (missing postcode coordinates). They still appear in the table above.
        </p>
      ) : null}
    </section>
  );
};

export default TenantsGeographyStateView;
